import { usePrice } from '@corratech/pylot-price'
import { formatCurrency } from '@lib/utils/formatCurrency'
import type {
    CartItemPrices,
    CartPrices,
    Maybe,
    PriceRange
} from '@pylot-data/pylotschema'
import cn from 'classnames'
import s from './ProductPrice.module.scss'
import type { ReactElement, ReactNode } from 'react'

export type ProductPrice = {
    priceRange: Maybe<CartPrices | CartItemPrices | PriceRange>
    className?: string
    customTotal?: number
    quantity?: number
    shouldDisplayPriceRange?: boolean
    hideDiscountPercent?: boolean
    isAutocomplete?: boolean
    /** Rendered beside the original/regular price (e.g. PLP review rating). */
    asideContent?: ReactNode
    /** Tile layout (PLP / CMS): discount below price at 12px. */
    isPlp?: boolean
}

export const ProductPrice = ({
    priceRange,
    className,
    customTotal,
    quantity,
    shouldDisplayPriceRange = false,
    hideDiscountPercent = false,
    isAutocomplete = false,
    asideContent,
    isPlp = false
}: ProductPrice): ReactElement => {
    let maxPriceCurrency = ''
    let maxFinalCurrency = ''
    if (shouldDisplayPriceRange) {
        const maxPrice = (priceRange as PriceRange)?.maximum_price
            ?.regular_price?.value
        maxPriceCurrency = formatCurrency(String(maxPrice))

        const maxFinal = (priceRange as PriceRange)?.maximum_price?.final_price
            ?.value

        maxFinalCurrency = formatCurrency(String(maxFinal))
    }
    const discountValue = priceRange?.minimum_price?.discount?.amount_off

    const regularPrice = priceRange?.minimum_price?.regular_price?.value

    const { subtotal: minSubtotal, total: minTotal } = usePrice(priceRange)

    const minTotalCurrency = formatCurrency(
        customTotal || minTotal,
        undefined,
        quantity
    )
    const minSubtotalCurrency = formatCurrency(
        minSubtotal || minTotal,
        undefined,
        quantity
    )

    let hasDisccount = discountValue !== 0

    let discount = 0
    if (discountValue && regularPrice) {
        discount = Math.round((discountValue / regularPrice) * 100)
    }

    let customDiscount = 0
    if (discount == 0) {
        const totalWithDiscount = Number(
            minTotalCurrency.replace('$', '').replace('.', '')
        )
        const total = Number(
            minSubtotalCurrency.replace('$', '').replace('.', '')
        )

        const result = total - totalWithDiscount
        customDiscount = Math.round((result / total) * 100)
        hasDisccount = !!customDiscount
    }

    if (shouldDisplayPriceRange && minSubtotalCurrency !== maxPriceCurrency) {
        return (
            <div
                className={cn(
                    s['product-price'],
                    s[`${className ? className : ''}`],
                    { [s['product-price-plp']]: isPlp }
                )}
                tabIndex={0}
                role="button"
            >
                <div className={s['original-price-row']}>
                    <span
                        className={cn(
                            s['subtotal-price'],
                            s[`${hasDisccount ? 'old-price' : ''}`]
                        )}
                    >
                        {minSubtotalCurrency} - {maxPriceCurrency}
                    </span>
                    {asideContent}
                </div>
                {hasDisccount ? (
                    <div
                        className={cn('flex flex-col items-start', {
                            [s['row-discount']]: isPlp
                        })}
                    >
                        <span className={s['total-price-config']}>
                            {minTotalCurrency} - {maxFinalCurrency}
                        </span>
                        {!hideDiscountPercent && (
                            <span className={s['discount-config']}>
                                {`(-${
                                    discount == 0 ? customDiscount : discount
                                }%${!isAutocomplete ? ' descuento' : ''})`}
                            </span>
                        )}
                    </div>
                ) : null}
            </div>
        )
    }

    return (
        <div
            className={cn(
                s['product-price'],
                s[`${className ? className : ''}`],
                { [s['product-price-plp']]: isPlp }
            )}
            tabIndex={0}
            role="button"
        >
            <div className={s['original-price-row']}>
                <span
                    className={cn(
                        s['subtotal-price'],
                        s[`${hasDisccount ? 'old-price' : ''}`]
                    )}
                >
                    {minSubtotalCurrency}
                </span>
                {asideContent}
            </div>
            {hasDisccount ? (
                <div className={s['row-discount']}>
                    <span className={s['total-price']}>{minTotalCurrency}</span>
                    {!hideDiscountPercent && (
                        <span className={s['discount']}>
                            {`(-${
                                discount == 0 ? customDiscount : discount
                            }%${!isAutocomplete ? ' descuento' : ''})`}
                        </span>
                    )}
                </div>
            ) : null}
        </div>
    )
}
